import React from 'react';
import { buildGrowthModel } from './plantingGrowthUtils';
import { planPreviewRequest } from './plantingPlanDisplay';

export function PlantingGrowthStep({
  answers,
  place = null,
  acres = 0,
  saved = false,
  preferKey = '',
  draftSoilPh = '',
  preview = null,
  loadPlan,
}) {
  const [fetched, setFetched] = React.useState(null);

  React.useEffect(() => {
    if (preview) {
      setFetched(preview);
      return undefined;
    }
    if (typeof loadPlan !== 'function') {
      setFetched(null);
      return undefined;
    }
    let cancelled = false;
    setFetched(null);
    (async () => {
      try {
        const payload = await loadPlan(planPreviewRequest({
          answers,
          place,
          acres,
          blend: preferKey,
          draftSoilPh,
        }));
        if (!cancelled) setFetched(payload || null);
      } catch (err) {
        console.error('Growth plan preview failed', err);
        if (!cancelled) setFetched(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [preview, answers, place, acres, preferKey, draftSoilPh, loadPlan]);

  const model = buildGrowthModel({
    answers,
    place,
    acres,
    saved,
    preferKey,
    preview: preview || fetched,
  });

  return (
    <section className="plant-growth" data-testid="plant-growth-step">
      <div className="plant-growth-card">
        <div className="plant-growth-card__head">
          <span>IF YOU PLANT {model.hero}</span>
          <span className="plant-growth-card__accent" data-testid="plant-growth-accent">{model.accent}</span>
        </div>
        <div className="plant-growth-stages">
          {model.stages.map((stage) => (
            <div key={stage.num} className="plant-growth-stage">
              <div className="plant-growth-stage__rail">
                <span
                  className="plant-growth-stage__num"
                  style={{ background: stage.bg, color: stage.fg }}
                >
                  {stage.num}
                </span>
                <span
                  className="plant-growth-stage__rule"
                  style={{ background: stage.rule }}
                  aria-hidden="true"
                />
              </div>
              <div className="plant-growth-stage__body">
                <div className="plant-growth-stage__when">{stage.when}</div>
                <div className="plant-growth-stage__title">{stage.title}</div>
                <p className="plant-growth-stage__text">{stage.text}</p>
              </div>
              <div className="plant-growth-blades" aria-hidden="true">
                {stage.blades.map((blade, idx) => (
                  <span
                    key={idx}
                    style={{ height: blade.h, background: blade.color }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="plant-growth__grid">
        <div className="plant-growth-card">
          <div className="plant-growth-card__head">WHAT COULD THROW IT OFF</div>
          <div className="plant-growth-risks">
            {model.risks.map((risk) => (
              <div key={risk.title} className="plant-growth-risk">
                <span
                  className="plant-growth-risk__bar"
                  style={{ background: risk.color }}
                  aria-hidden="true"
                />
                <span className="plant-growth-risk__body">
                  <span className="plant-growth-risk__title">{risk.title}</span>
                  <span className="plant-growth-risk__text">{risk.text}</span>
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="plant-growth-summary">
          <div className="plant-growth-summary__kicker">THE WHOLE PLAN</div>
          <div className="plant-growth-summary__grid">
            {model.summary.map((item) => (
              <div key={item.label} className="plant-growth-summary__tile">
                <div className="plant-growth-summary__label">{item.label}</div>
                <div className="plant-growth-summary__value">{item.value}</div>
              </div>
            ))}
          </div>
          {model.saved ? (
            <div className="plant-growth-stamp" data-testid="plant-growth-saved-stamp">
              <span>SAVED TO MY PLOTS</span>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
